import type { CardId, WallCard, WallId } from '@walls/store/types';
import type { WallsClient } from './client';

export class ClustersApi {
  constructor(private readonly client: WallsClient) {}

  insertCluster = (
    boardId: WallId,
    title: string,
    color: string,
    position: { height?: number; width?: number; xPos: number; yPos: number },
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(`/boards/clusters/${boardId}`, boardId, key, {
      method: 'POST',
      body: JSON.stringify({
        color,
        height: position.height,
        title,
        width: position.width,
        xPos: position.xPos,
        yPos: position.yPos,
      }),
    });
  };

  updateClusterPosition = (
    boardId: WallId,
    clusterId: CardId,
    position: { clusterId: CardId; xPos: number; yPos: number },
    key?: string | null
  ) => {
    return this.client.requestJson<WallCard>(
      `/boards/clusters/position/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify(position),
      }
    );
  };

  addCardToCluster = (
    boardId: WallId,
    clusterId: CardId,
    cardId: CardId,
    position: number,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/cards/${boardId}/${clusterId}/${cardId}`,
      boardId,
      key,
      {
        method: 'POST',
        body: JSON.stringify({ position }),
      }
    );
  };

  detachCardFromCluster = (
    boardId: WallId,
    clusterId: CardId,
    cardId: CardId,
    position: { xPos: number; yPos: number },
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/cards/${boardId}/${clusterId}/${cardId}`,
      boardId,
      key,
      {
        method: 'DELETE',
        body: JSON.stringify(position),
      }
    );
  };

  createClusterFromCard = (
    boardId: WallId,
    parentCardId: CardId,
    childCardId: CardId,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/${boardId}/${parentCardId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({
          action: 'create',
          cards: [{ id: childCardId }],
        }),
      }
    );
  };

  addClusterToCluster = (
    boardId: WallId,
    targetClusterId: CardId,
    sourceClusterId: CardId,
    collapsed: boolean,
    position: number,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/clusters/${boardId}/${targetClusterId}/${sourceClusterId}`,
      boardId,
      key,
      {
        method: 'POST',
        body: JSON.stringify({ collapsed, position }),
      }
    );
  };

  detachClusterFromCluster = (
    boardId: WallId,
    parentClusterId: CardId,
    childClusterId: CardId,
    position: { collapsed: boolean; xPos: number; yPos: number },
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/clusters/${boardId}/${parentClusterId}/${childClusterId}`,
      boardId,
      key,
      {
        method: 'DELETE',
        body: JSON.stringify(position),
      }
    );
  };

  collapseCluster = (boardId: WallId, clusterId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/clusters/collapse/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
      }
    );
  };

  expandCluster = (boardId: WallId, clusterId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/clusters/expand/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
      }
    );
  };

  lockCluster = (boardId: WallId, clusterId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/clusters/lock/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
      }
    );
  };

  unlockCluster = (boardId: WallId, clusterId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(
      `/boards/clusters/unlock/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
      }
    );
  };

  updateCluster = (
    boardId: WallId,
    clusterId: CardId,
    title: string,
    content: string,
    color: string,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(`/boards/clusters/${boardId}/${clusterId}`, boardId, key, {
      method: 'PUT',
      body: JSON.stringify({
        action: 'update',
        color,
        content,
        title,
      }),
    });
  };

  deleteCluster = (boardId: WallId, clusterId: CardId, key?: string | null) => {
    return this.client.requestJson<void>(`/boards/clusters/${boardId}/${clusterId}`, boardId, key, {
      method: 'DELETE',
    });
  };

  sortCluster = (
    boardId: WallId,
    clusterId: CardId,
    cards: Array<{ id: CardId }>,
    key?: string | null
  ) => {
    return this.client.requestJson<void>(
      `/boards/clusters/sort/${boardId}/${clusterId}`,
      boardId,
      key,
      {
        method: 'PUT',
        body: JSON.stringify({ cards, clusterId }),
      }
    );
  };
}
